import { useCallback, useEffect, useRef, useState } from "react";
import { ConfirmButton } from "@/components/common/ConfirmButton";
import { TextInputField } from "@/components/common/TextInputField";

interface RenameChatDialogProps {
	open: boolean;
	initialName: string;
	onConfirm: (newName: string) => Promise<void>;
	onCancel: () => void;
}

export function RenameChatDialog({
	open,
	initialName,
	onConfirm,
	onCancel,
}: RenameChatDialogProps) {
	const dialogRef = useRef<HTMLDialogElement>(null);
	const [name, setName] = useState(initialName);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		const el = dialogRef.current;
		if (!el) return;
		if (open && !el.open) {
			el.showModal();
			setName(initialName);
			setLoading(false);
			setError(null);
		} else if (!open && el.open) {
			el.close();
		}
	}, [open, initialName]);

	const trimmed = name.trim();
	const canConfirm = trimmed.length > 0 && trimmed !== initialName && !loading;

	const handleConfirm = useCallback(async () => {
		if (!canConfirm) return;
		setLoading(true);
		setError(null);
		try {
			await onConfirm(trimmed);
		} catch (err) {
			setError(err instanceof Error ? err.message : "エラーが発生しました");
			setLoading(false);
		}
	}, [canConfirm, onConfirm, trimmed]);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === "Escape" && !loading) onCancel();
		},
		[onCancel, loading],
	);

	if (!open) return null;

	return (
		<dialog
			ref={dialogRef}
			onKeyDown={handleKeyDown}
			onCancel={(e) => {
				e.preventDefault();
				if (!loading) onCancel();
			}}
			onClick={(e) => {
				// バックドロップ (= dialog 要素自身) クリックで閉じる「ライトディスミス」。
				if (e.target === e.currentTarget && !loading) onCancel();
			}}
			className="fixed inset-0 z-50 m-auto w-[360px] rounded-4 border border-line-1 bg-surface-1 p-0 shadow-popover backdrop:bg-scrim"
			data-testid="rename-chat-dialog"
		>
			<form
				onSubmit={(e) => {
					e.preventDefault();
					void handleConfirm();
				}}
			>
				<div className="px-5 pt-5 pb-4">
					<h3 className="text-ui font-semibold text-fg-1">チャット名を変更</h3>
					<div className="mt-3">
						<TextInputField
							label="名前"
							value={name}
							onChange={setName}
							error={error ?? undefined}
						/>
					</div>
				</div>
				<div className="flex justify-end gap-2 border-t border-line-1 px-5 py-3">
					<button
						type="button"
						onClick={onCancel}
						disabled={loading}
						className="rounded-2 px-3 py-1.5 text-caption text-fg-2 hover:bg-surface-2 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
						data-testid="rename-chat-dialog-cancel"
					>
						キャンセル
					</button>
					<ConfirmButton
						label="決定"
						type="submit"
						loading={loading}
						disabled={!canConfirm}
						size="sm"
						data-testid="rename-chat-dialog-confirm"
					/>
				</div>
			</form>
		</dialog>
	);
}
